import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";

const NAV_LINKS = [
  { to: "/features", label: "Features", testId: "nav-features-link" },
  { to: "/faq", label: "FAQ", testId: "nav-faq-link" },
  { to: "/about", label: "About", testId: "nav-about-link" },
];

export function BookDemoButton({ className = "", testId = "nav-book-demo-button", label = "Book a Demo" }) {
  return (
    <Link
      to="/about#contact"
      data-testid={testId}
      className={`inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-6 py-2.5 text-sm font-semibold tracking-tight hover:bg-slate-700 transition-colors ${className}`}
      style={{ fontFamily: "Outfit" }}
    >
      {label}
      <ArrowUpRight size={16} strokeWidth={2.5} />
    </Link>
  );
}

function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/70 border-b border-slate-200" data-testid="marketing-navbar">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="marketing-logo-link" className="flex items-center gap-2.5">
          <img src="/logo.png" alt="Post Your Content" className="w-8 h-8" />
          <span className="text-slate-900 font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
            Post Your Content
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              className={`text-sm font-medium transition-colors hover:text-slate-900 ${
                location.pathname === l.to ? "text-slate-900" : "text-slate-500"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-5">
          <a href="/app" data-testid="nav-login-link" className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
            Log in
          </a>
          <BookDemoButton />
        </div>
        <button
          className="md:hidden text-slate-900"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white px-6 py-6 flex flex-col gap-5" data-testid="mobile-menu-panel">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-slate-700 font-medium text-base" data-testid={`mobile-${l.testId}`}>
              {l.label}
            </Link>
          ))}
          <a href="/app" data-testid="mobile-nav-login-link" className="text-slate-700 font-medium text-base">
            Log in
          </a>
          <BookDemoButton testId="mobile-nav-book-demo-button" className="justify-center" />
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-slate-900 text-slate-300" data-testid="marketing-footer">
      <div className="max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-2.5 mb-4">
            <img src="/logo.png" alt="" className="w-7 h-7" />
            <span className="text-white font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
              Post Your Content
            </span>
          </div>
          <p className="text-sm text-slate-400 max-w-sm leading-relaxed">
            AI-powered. Human-approved. The social media management platform that keeps your team in
            control, no matter how much of the work Shee does for you.
          </p>
        </div>
        <div className="md:col-span-3">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">Explore</p>
          <ul className="space-y-3 text-sm">
            <li><Link to="/features" className="hover:text-white transition-colors">Features</Link></li>
            <li><Link to="/faq" className="hover:text-white transition-colors">FAQ</Link></li>
            <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
          </ul>
        </div>
        <div className="md:col-span-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">Get in touch</p>
          <a
            href="mailto:hello@postyourcontent.com"
            className="text-sm hover:text-white transition-colors block mb-4"
            data-testid="footer-contact-email"
          >
            hello@postyourcontent.com
          </a>
          <div className="flex gap-4 text-xs text-slate-500">
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-white transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
      <div className="border-t border-slate-800 py-6 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} Post Your Content. All rights reserved.
      </div>
    </footer>
  );
}

export default function MarketingLayout({ children }) {
  return (
    <div className="min-h-screen bg-white text-slate-900" style={{ fontFamily: "Inter" }} data-testid="marketing-site-root">
      <Navbar />
      <main className="pt-16">{children}</main>
      <Footer />
    </div>
  );
}
